import type { HowItWorksProps } from '@/components/giveback/HowItWorks';
import type { InternalImpactProps } from '@/components/giveback/InternalImpact';
import type { JoinTheGivebackProps } from '@/components/giveback/JoinTheGiveback';
import type { ImpactSectionProps } from '@/components/giveback/OurCommunityImpact';
import type { OurImpact2024Props } from '@/components/giveback/OurImpact2024';
import type { PublicBenefitProps } from '@/components/giveback/PublicBenefit';
import type { WhatIsGivebackProps } from '@/components/giveback/WhatIsGiveback';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/giveback/OG-2025.png';
import impact_1 from '@/static/assets/giveback_2024/impact_1.svg';
import impact_1_mobile from '@/static/assets/giveback_2024/impact_1_mobile.svg';
import impact_2 from '@/static/assets/giveback_2024/impact_2.svg';
import impact_2_mobile from '@/static/assets/giveback_2024/impact_2_mobile.svg';
import impact_3 from '@/static/assets/giveback_2024/impact_3.svg';
import impact_3_mobile from '@/static/assets/giveback_2024/impact_3_mobile.svg';
import impact_4 from '@/static/assets/giveback_2024/impact_4.svg';
import impact_4_mobile from '@/static/assets/giveback_2024/impact_4_mobile.svg';
import impact_5 from '@/static/assets/giveback_2024/impact_5.svg';
import impact_5_mobile from '@/static/assets/giveback_2024/impact_5_mobile.svg';
import works_1 from '@/static/assets/giveback_2024/works_1.svg';
import works_1_mobile from '@/static/assets/giveback_2024/works_1_mobile.svg';
import works_2 from '@/static/assets/giveback_2024/works_2.svg';
import works_2_mobile from '@/static/assets/giveback_2024/works_2_mobile.svg';
import type { BasePageConfig } from '../../BasePageConfig';

export interface Config extends BasePageConfig {
  whatIsGiveback: WhatIsGivebackProps;
  howItWorks: HowItWorksProps;
  ourImpact2024: OurImpact2024Props;
  ourCommunityImpact: ImpactSectionProps;
  joinTheGiveback: JoinTheGivebackProps;
  publicBenefit: PublicBenefitProps;
  internalImpact: InternalImpactProps;
}

export const config: Config = {
  seo: {
    title: 'The Lemonade Giveback and Social Impact',
    metaTitle: 'The Lemonade Giveback and Social Impact',
    description: 'Throughout the year, your coverage helps fund real impact. Check out how it works.',
    ogImage: ogImage.src,
    uniqueCanonicalUrl: 'https://www.lemonade.com/giveback', // TODO: remove this when the page is translated
  },
  layout: { ...sharedConfig['en-US'].layout },
  whatIsGiveback: {
    title: 'What is the Lemonade Giveback?',
    p1: 'Giveback is at the heart of Lemonade. When everyone knows that your insurance connects to causes people care about, it creates a system where honesty, fairness, and trust naturally come through when it’s time to file and handle claims.',
    p2: `Since 2017, we've donated over $12M to help build new homes, provide access to clean water, support animal rights, and more. So your policy doesn't just protect you, it connects your choices to real community support, bringing out the best in all of us.`,
  },
  howItWorks: {
    title: 'How Giveback Works',
    steps: [
      {
        text: 'You get a Lemonade policy, and\nselect a cause you care about',
        image: works_1,
        mobileImage: works_1_mobile,
      },
      {
        text: 'Throughout the year, your coverage helps fund real impact',
        image: works_2,
        mobileImage: works_2_mobile,
      },
    ],
  },
  ourImpact2024: {
    title: 'Our Impact in 2025',
    subtitle: '45 nonprofits will be supported this year via the Lemonade Giveback.',
    years: [
      { url: '/blog/time-to-giveback', year: '2017' },
      { url: '/blog/lemonade-giveback-2018/', year: '2018' },
      { url: '/giveback-2019', year: '2019' },
      { url: '/giveback-2020', year: '2020' },
      { url: '/giveback-2021', year: '2021' },
      { url: '/giveback-2022', year: '2022' },
      { url: '/giveback-2023', year: '2023' },
      { url: 'https://www.lemonade.com/blog/lemonade-2024-giveback/', year: '2024' },
    ],

    charities: [
      {
        org: 'Trees For All',
        title: '1,100 trees',
        desc: 'planted to restore forests and absorb carbon emissions.',
      },
      {
        org: 'March For Our Lives',
        title: '650 young leaders',
        desc: 'across 53 campus hubs trained in critical information on how to prevent gun violence.',
      },
      {
        org: 'Foster Love',
        title: '175 foster youth',
        desc: 'reunited with siblings at the Disney Days event.',
      },
      {
        org: 'UNICEF',
        title: '151 health kits',
        desc: 'sent to families in crisis zones.',
      },
      {
        org: 'Plastic Soup Foundation',
        title: '200 kg of plastic waste',
        desc: 'prevented from polluting oceans.',
      },
      {
        org: 'Teach For America',
        title: '294,200 students',
        desc: 'supported by 4,509 teachers and 262 Ignite Fellows in 1,717 high-need schools.',
      },
      {
        org: 'Direct Relief',
        title: '4,000 people',
        desc: 'received emergency aid and critical health supplies.',
      },
      {
        org: 'Kika',
        title: '1 childhood cancer research trial',
        desc: 'funded in the Netherlands.',
      },
      {
        org: 'The Ocean Agency',
        title: '50,000 ocean scientists',
        desc: 'and conservation organizations supported to help protect our ocean.',
      },
      {
        org: 'Redi School of Digital Integration',
        title: '20 refugees and migrants',
        desc: 'provided with digital skills training and mentorship.',
      },
      {
        org: 'One Earth One Ocean',
        title: '100 kg of trash',
        desc: 'collected from coastal waters, protecting marine life.',
      },
      {
        org: 'Girls Talk',
        title: '250 mental health workshops',
        desc: 'funded for young girls from underserved communities.',
      },
      {
        org: 'Care',
        title: '28 emergency food baskets',
        desc: 'delivered to families in crisis.',
      },
      {
        org: 'Robin Hood',
        title: '13,000 nourishing meals',
        desc: 'delivered to low-income families in New York City.',
      },
      {
        org: 'Viva Con Agua',
        title: '30 people',
        desc: 'provided with clean drinking water and sanitation.',
      },
      {
        org: 'American Forests',
        title: '15,600 trees',
        desc: 'planted to help reach the goal of 4 million trees across North America.',
      },
      {
        org: 'The French League Against Cancer',
        title: '30 cancer patients',
        desc: 'received physiotherapy treatment to aid recovery.',
      },
      {
        org: 'En Avant Toute(s)',
        title: '70 support sessions',
        desc: 'delivered to victims of abuse in France.',
      },
      {
        org: 'LSVD',
        title: '600 meals',
        desc: 'provided to at-risk LGBTQ+ youth in France.',
      },
      {
        org: 'Malala Fund',
        title: '1,500 female headteachers',
        desc: 'trained to support education for 150,000 girls.',
      },
      {
        org: 'Charity: Water',
        title: '4,000 people',
        desc: 'in Uganda were provided with access to clean water—for life.',
      },
      {
        org: 'National Breast Cancer Foundation',
        title: '2,560 Breast Health Kits',
        desc: 'health kits and 10 mammograms provided.',
      },
      {
        org: 'Deepwave E.V.',
        title: '400 kg of plastic waste',
        desc: 'removed from oceans and coastlines.',
      },
      {
        org: 'A21',
        title: '3 months',
        desc: 'of anti-trafficking hotlines funded in South Africa.',
      },
      {
        org: 'New Story',
        title: '50 underserved families',
        desc: 'empowered to transform their lives through land and home ownership.',
      },
      {
        org: 'The Trevor Project',
        title: '1,200 LGBTQ+ youth',
        desc: 'provided with free 24/7 mental health crisis counseling.',
      },
      {
        org: 'GOSH',
        title: "1 new Children's Cancer centre",
        desc: "supported to save more children's lives",
      },
      {
        org: 'Pencils of Promise',
        title: '4,200 students across 13 schools',
        desc: 'provided with menstrual health supplies.',
      },
      {
        org: 'Mind',
        title: '100 people',
        desc: 'received mental health support through helplines.',
      },
      {
        org: 'De Regenboog Groep',
        title: '400 warm meals and shelter services',
        desc: 'delivered to people experiencing homelessness in Amsterdam.',
      },
      {
        org: 'Feeding America',
        title: '152,320 meals',
        desc: 'distributed to food-insecure families across the U.S.',
      },
      {
        org: 'Clean Clothes Campaign',
        title: '1 campaign',
        desc: 'funded to support garment supply chain workers.',
      },
      {
        org: 'Win',
        title: '3,525 homeless youth',
        desc: 'received STEM and college prep support.',
      },
      {
        org: 'Nationaal Ouderenfonds',
        title: '50 isolated seniors',
        desc: 'engaged through social activities and companionship, reducing loneliness.',
      },
      {
        org: 'KNGF Geleidehonden',
        title: '1 guide dog',
        desc: 'trained to assist a visually impaired person.',
      },
      {
        org: 'Cure Childhood Cancer',
        title: '20 families',
        desc: 'devastated by a childhood cancer diagnosis had their rent paid for.',
      },
      {
        org: 'Pro Wildlife',
        title: '1 wildlife protection campaign',
        desc: 'supported in Germany.',
      },
      {
        org: 'MEET',
        title: '21 underserved students',
        desc: 'joined a 3-year tech and entrepreneurship program.',
      },
      {
        org: 'Habitat For Humanity',
        title: '30 families',
        desc: 'in the US helped with the construction of safe, affordable homes.',
      },
      {
        org: 'Plan International',
        title: 'Over 80 countries',
        desc: 'reached with support for children and girls, helping create a world where we are all equal.',
      },
      {
        org: 'Citymeals On Wheels',
        title: '2,300 nutritious meals',
        desc: 'delivered to homebound elderly New Yorkers.',
      },
      {
        org: 'American Red Cross',
        title: '65,000 disasters a year responded to',
        desc: 'providing help and hope to those in need.',
      },
      {
        org: 'Les Restos Du Coeur',
        title: '600 meals',
        desc: 'served to people in need across France.',
      },
      {
        org: 'Hôpitaux De Paris Hôpitaux De France',
        title: '10 medical staff',
        desc: 'supported with a relaxation area in CHU.',
      },
    ],
  },
  ourCommunityImpact: {
    title: 'Our Community’s Impact',
    subtitle:
      "To date, Lemonade has donated over $12M to causes chosen by our customers.\n Here are some of the ways we're making a difference.",
    impactItems: [
      {
        title: 'Support Families In Need',
        impact: '769,220 meals',
        desc: 'delivered to high-need communities.',
        image: impact_4,
        imageMobile: impact_4_mobile,
      },
      {
        title: 'Rescue Animals',
        impact: '128,568 animals',
        desc: 'received life-saving care.',
        image: impact_5,
        imageMobile: impact_5_mobile,
      },
      {
        title: 'Uplift Communities',
        impact: '91,997 veterans',
        desc: 'supported with PTSD training.',
        image: impact_3,
        imageMobile: impact_3_mobile,
      },
      {
        title: 'Protect The Planet',
        impact: '44,866 people',
        desc: 'given safe drinking water for life.',
        image: impact_1,
        imageMobile: impact_1_mobile,
      },
      {
        title: 'Emergency Response',
        impact: '167,910 people',
        desc: 'received life-saving medical care.',
        image: impact_2,
        imageMobile: impact_2_mobile,
      },
    ],
  },
  publicBenefit: {
    title: 'We are a Public Benefit Corporation',
    p1: "Lemonade is a Public Benefit Corporation and a certified B Corp, meaning we're legally committed to making a positive social impact. The B Corp certification is awarded to businesses that prove they balance profit with purpose, focusing on people and the planet. Every three years, we undergo a comprehensive assessment of our entire business.",
  },
  internalImpact: {
    title: 'Internal Impact',
    p1: 'Globally, our Makers are involved in diverse impact initiatives, focusing on education, volunteering, and employee mentorship. We collaborate with local nonprofits to support our communities and prioritize using local and sustainable suppliers for office events.',
  },
  joinTheGiveback: {
    title: 'Join Us',
    subtitle:
      'Tens of thousands of policyholders are already supporting causes they care about just by having a Lemonade insurance policy. As our community grows, your social impact can become even more powerful.',
    mainCtaLabel: 'Get insured and giveback',
  },
};
